import React, { useEffect, useState } from 'react';
import { XCircle, ArrowLeft, RefreshCw, AlertTriangle, CreditCard, Home, Phone } from 'lucide-react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';

const PagamentoErro: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [errorData, setErrorData] = useState<any>(null);

  useEffect(() => {
    // Primeiro, tentar pegar dados do location.state (vindo do PagamentoRetorno)
    if (location.state) {
      setErrorData(location.state);
      console.log('Dados recebidos via state:', location.state);
      return;
    }

    // Fallback: capturar parâmetros da URL do Mercado Pago (caso acesse diretamente)
    const status = searchParams.get('status');
    const paymentId = searchParams.get('payment_id');
    const preferenceId = searchParams.get('preference_id');
    const externalReference = searchParams.get('external_reference');

    setErrorData({
      status,
      paymentId,
      preferenceId,
      externalReference
    });

    // Log para debug
    console.log('Pagamento com erro:', {
      status,
      paymentId,
      preferenceId,
      externalReference
    });
  }, [searchParams, location.state]);

  const handleVoltarInicio = () => {
    navigate('/');
  };

  const handleTentarNovamente = () => {
    // Voltar para a página de checkout
    navigate(-1);
  };

  const getErrorMessage = (status: string) => {
    switch (status) {
      case 'rejected':
        return 'Seu pagamento foi rejeitado. Verifique os dados do cartão e tente novamente.';
      case 'cancelled':
        return 'O pagamento foi cancelado. Você pode tentar novamente quando desejar.';
      case 'in_process':
        return 'Seu pagamento está sendo processado. Aguarde a confirmação.';
      default:
        return 'Ocorreu um erro durante o processamento do pagamento. Tente novamente.';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full">
        {/* Header de Erro */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-red-100 rounded-full mb-6">
            <XCircle className="w-12 h-12 text-red-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            ❌ Pagamento Não Aprovado
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            {errorData?.status ? getErrorMessage(errorData.status) : 'Ocorreu um erro durante o pagamento.'}
          </p>
          <p className="text-sm text-gray-500">
            Não se preocupe, você pode tentar novamente
          </p>
        </div>

        {/* Card Principal */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <div className="ml-4">
              <h2 className="text-2xl font-bold text-gray-900">Transação Não Processada</h2>
              <p className="text-gray-600">Seu pagamento não foi aprovado</p>
            </div>
          </div>

          {errorData && (
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CreditCard className="w-5 h-5 mr-2" />
                Detalhes da Transação
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="font-medium text-red-600 bg-red-100 px-2 py-1 rounded-full text-sm capitalize">
                      {errorData.status || 'Erro'}
                    </span>
                  </div>
                  {errorData.paymentId && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">ID do Pagamento:</span>
                      <span className="font-medium text-gray-900 text-sm">
                        {errorData.paymentId}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  {errorData.preferenceId && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Referência:</span>
                      <span className="font-medium text-gray-900 text-sm">
                        {errorData.preferenceId}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Data:</span>
                    <span className="font-medium text-gray-900 text-sm">
                      {new Date().toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Possíveis Causas */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-yellow-900 mb-4 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Possíveis Causas
            </h3>
            <ul className="space-y-2 text-yellow-800">
              <li className="flex items-center">
                <XCircle className="w-4 h-4 text-yellow-600 mr-2" />
                Dados do cartão incorretos
              </li>
              <li className="flex items-center">
                <XCircle className="w-4 h-4 text-yellow-600 mr-2" />
                Cartão sem limite disponível
              </li>
              <li className="flex items-center">
                <XCircle className="w-4 h-4 text-yellow-600 mr-2" />
                Problemas de conectividade
              </li>
              <li className="flex items-center">
                <XCircle className="w-4 h-4 text-yellow-600 mr-2" />
                Cartão bloqueado pelo banco
              </li>
            </ul>
          </div>

          {/* Ações */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={handleTentarNovamente}
              className="flex justify-center items-center px-6 py-4 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Tentar Novamente
            </button>
            
            <Link
              to="/"
              className="flex justify-center items-center px-6 py-4 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Home className="w-5 h-5 mr-2" />
              Voltar ao Início
            </Link>
          </div>
        </div>

        {/* Suporte */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-start">
            <Phone className="w-6 h-6 text-blue-600 mr-3 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Precisa de Ajuda?
              </h3>
              <p className="text-gray-600 mb-4">
                Se o problema persistir, nossa equipe de suporte está pronta para ajudar.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Contato:</strong> Entre em contato conosco através do e-mail de suporte 
                  ou pelo chat online disponível no sistema.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PagamentoErro;
